/*
Crédits photos — écran dynamique dérivé du manifeste species-photos.json.
Parité Android `PhotoCreditsScreen.kt` (`buildPhotoCreditsModel` + écran).

Contrairement à About/Privacy/Terms (data-only via InfoScreen), le contenu
est calculé au runtime depuis le repository photos : la source Ville de Paris
est repliée en bloc collectif (compteurs, attribution ODbL unique partagée),
tandis que les cascades Wikimedia/iNaturalist sont listées photo par photo
(obligation d'attribution par auteur). D'où un écran dédié.
*/

import { navigate, kPaths } from '../router'
import {
	ensureSpeciesPhotos,
	allSpeciesPhotos,
	licenseFor,
	sourceFor,
} from '../data/speciesPhotos'
import {
	ensureSpeciesIndex,
	speciesNameFor,
	displayName,
} from '../data/speciesIndex'

// Clés sources/licences — parité constantes Kotlin (SRC_*, LICENSE_ODBL).
const kSrcParis = 'paris'
const kSrcWikimedia = 'wikimedia-commons'
const kSrcInaturalist = 'inaturalist'
const kLicenseOdbl = 'odbl-1.0'
// Fallback du titre de la card Paris si la source manque au manifeste
// (le nom canonique est lu depuis sourceFor('paris').name).
const kParisTitleFallback = 'Ville de Paris — Guide des essences 2024'

/* Une ligne de crédit par photo cascade (Wikimedia / iNaturalist). */
interface CreditRow
{
	speciesName: string
	author: string
	licenseName: string
	url: string | null
}

/*
Modèle de l'écran crédits, dérivé une fois du repository photos. Paris →
compteurs collectifs ; cascades → une CreditRow par photo, triées (nom
casse-insensible, sk) — parité Kotlin `PhotoCreditsModel`.
*/
interface PhotoCreditsModel
{
	parisPhotoCount: number
	parisSpeciesCount: number
	wikimediaRows: CreditRow[]
	inatRows: CreditRow[]
}

/*
Construit le modèle crédits (fonction pure). Paris → compteurs collectifs
(toutes photos, principales + détails). Cascades → une CreditRow par photo,
nom d'espèce résolu via l'index (cascade displayName : nv → nc → binôme,
fallback « Espèce #{sk} »), licence résolue via licenseFor (fallback = clé
brute). Une source inconnue (ni paris ni les deux cascades) est ignorée
silencieusement — parité Kotlin (`when(photo.source)` sans branche `else`).
Tri final par (nom casse-insensible, sk) pour un ordre stable et déterministe.
*/
function buildPhotoCreditsModel(): PhotoCreditsModel
{
	let vParisPhotoCount = 0
	let vParisSpeciesCount = 0
	const vWikimedia: { sk: number; row: CreditRow }[] = []
	const vInat: { sk: number; row: CreditRow }[] = []

	for (const [vSk, vPhotos] of allSpeciesPhotos())
	{
		// La source est portée par chaque photo mais partagée par espèce
		// (cascades disjointes) — le repliement Paris lit la principale.
		if (vPhotos.principal.src === kSrcParis)
		{
			vParisSpeciesCount++
			vParisPhotoCount += vPhotos.all.length
			continue
		}
		const vNameEntry = speciesNameFor(vSk)
		const vName = vNameEntry ? displayName(vNameEntry) : `Espèce #${vSk}`
		for (const vPhoto of vPhotos.all)
		{
			const vLicenseName = licenseFor(vPhoto.lic)?.name ?? vPhoto.lic
			const vRow: CreditRow = { speciesName: vName, author: vPhoto.by, licenseName: vLicenseName, url: vPhoto.u }
			if (vPhoto.src === kSrcWikimedia) vWikimedia.push({ sk: vSk, row: vRow })
			else if (vPhoto.src === kSrcInaturalist) vInat.push({ sk: vSk, row: vRow })
		}
	}

	return {
		parisPhotoCount: vParisPhotoCount,
		parisSpeciesCount: vParisSpeciesCount,
		wikimediaRows: sortedRows(vWikimedia),
		inatRows: sortedRows(vInat),
	}
}

/* Tri (nom casse-insensible, sk) puis projection sur la CreditRow — parité `sortedRows`. */
function sortedRows(inRows: { sk: number; row: CreditRow }[]): CreditRow[]
{
	return inRows
		.sort((inA, inB) => {
			const vNa = inA.row.speciesName.toLowerCase()
			const vNb = inB.row.speciesName.toLowerCase()
			if (vNa !== vNb) return vNa < vNb ? -1 : 1
			return inA.sk - inB.sk
		})
		.map((inPair) => inPair.row)
}

/*
Mount the screen. Shell synchrone (header retour + titre), puis hydrate async
(charge le manifeste photos + l'index espèces avant de calculer le modèle).
Accessible sans capture — pas de verrou.
*/
export function mountPhotoCreditsScreen(inRoot: HTMLElement): void
{
	inRoot.innerHTML = `
		<div class="screen info-screen">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Crédits photos</h1>
			</header>
			<div class="info-screen-body" id="credits-body"></div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		// About est l'écran parent naturel (la card « Crédits photos » y vit).
		if (history.length > 1) history.back()
		else navigate(kPaths.about())
	})
	void hydrate(inRoot)
}

async function hydrate(inRoot: HTMLElement): Promise<void>
{
	const vBody = inRoot.querySelector<HTMLElement>('#credits-body')
	if (!vBody) return

	await Promise.all([ensureSpeciesPhotos(), ensureSpeciesIndex()])
	const vModel = buildPhotoCreditsModel()

	vBody.innerHTML = `
		${renderParisCard(vModel)}
		${renderSection('Wikimedia Commons', vModel.wikimediaRows)}
		${renderSection('iNaturalist', vModel.inatRows)}
	`
}

/*
Bloc collectif Ville de Paris : nom du guide (lu depuis sourceFor, jamais
hardcodé — fallback = littéral Kotlin), compteur global + auteurs du Guide
des essences (sourceFor('paris').authors), ligne licence ODbL cliquable
(url depuis licenseFor). Parité `ParisCollectiveCard`.
*/
function renderParisCard(inModel: PhotoCreditsModel): string
{
	const vTitle = sourceFor(kSrcParis)?.name ?? kParisTitleFallback
	const vAuthors = sourceFor(kSrcParis)?.authors ?? ''
	const vOdblUrl = licenseFor(kLicenseOdbl)?.url ?? null
	const vMeta = `${inModel.parisPhotoCount} photos${vAuthors.length > 0 ? ` · Crédits ${vAuthors}` : ''}`
	return `
		<div class="credits-card species-card">
			<div class="credits-paris-title">${escapeHtml(vTitle)}</div>
			<div class="credits-paris-meta">${escapeHtml(vMeta)}</div>
			${renderLicenseLink('ODbL v1.0', vOdblUrl)}
		</div>
	`
}

/*
Section cascade (Wikimedia / iNaturalist) : en-tête « Nom (N) » + une ligne
par crédit. Parité Kotlin : l'en-tête est toujours rendu avec son compteur,
même à zéro row (aucune garde côté Kotlin) ; `items()` ne rend simplement
rien quand la liste est vide.
*/
function renderSection(inTitle: string, inRows: CreditRow[]): string
{
	return `
		<div class="credits-section-header">${escapeHtml(inTitle)} (${inRows.length})</div>
		${inRows.map(renderRow).join('')}
	`
}

/*
Ligne de crédit compacte : « espèce — auteur » puis lien licence, séparateur
fin en bas. Parité `CreditRowItem`.
*/
function renderRow(inRow: CreditRow): string
{
	return `
		<div class="credits-row">
			<div class="credits-row-identity">${escapeHtml(inRow.speciesName)} — ${escapeHtml(inRow.author)}</div>
			${renderLicenseLink(inRow.licenseName, inRow.url)}
		</div>
	`
}

/*
Libellé de licence : lien nouvel onglet quand une URL existe, sinon texte
discret. Parité `LicenseLink`.
*/
function renderLicenseLink(inLabel: string, inUrl: string | null): string
{
	if (!inUrl)
	{
		return `<span class="credits-row-license credits-row-license-plain">${escapeHtml(inLabel)}</span>`
	}
	return `<a class="credits-row-license" href="${escapeAttr(inUrl)}" target="_blank" rel="noopener">${escapeHtml(inLabel)} ↗</a>`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
